import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MapPin, TrendingUp, Target, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const BillboardAdvertisementDelhi = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Card className="p-8">
        <h1 className="text-3xl font-bold mb-6">
          Complete Guide to Billboard Advertising in Delhi: Locations, Costs, and Strategy (2024)
        </h1>

        <div className="prose lg:prose-xl">
          <p className="lead-paragraph text-lg text-gray-700 mb-6">
            Delhi, India's bustling capital and commercial hub, offers exceptional opportunities for billboard advertising. With a population exceeding 33.8 million and diverse commercial zones, Delhi's outdoor advertising landscape provides businesses with powerful ways to reach their target audience[3].
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4 flex items-center gap-2">
            <MapPin className="text-blue-600" />
            Prime Billboard Locations in Delhi
          </h2>

          <p className="mb-6">
            Connaught Place stands as Delhi's premier location for billboard advertising, being one of the largest commercial and business hubs in the city. The outer circle of Connaught Place receives massive footfall and vehicular traffic daily, making it an ideal choice for high-impact advertising campaigns[1].
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Commercial Areas</h3>
              <ul className="space-y-2">
                <li>Connaught Place</li>
                <li>Nehru Place</li>
                <li>Saket</li>
                <li>Laxmi Nagar</li>
                <li>Green Park</li>
              </ul>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Transit Hubs</h3>
              <ul className="space-y-2">
                <li>ESI Hospitals Metro Station</li>
                <li>Mayur Vihar Metro Station</li>
                <li>T3 Domestic Terminal</li>
                <li>Major Bus Terminals</li>
                <li>Railway Stations</li>
              </ul>
            </Card>
          </div>

          <Alert className="my-6 bg-blue-50">
            <AlertDescription>
              Now you can instantly book billboard spaces in Delhi through AdBoardBooking.com. Our platform offers real-time availability, transparent pricing, and automated booking management.
            </AlertDescription>
          </Alert>

          <h2 className="text-2xl font-semibold mt-8 mb-4 flex items-center gap-2">
            <Target className="text-blue-600" />
            Steps to Advertise on Delhi Billboards
          </h2>

          <ol className="space-y-4 mb-8">
            <li>
              <strong>Location Selection:</strong> Choose from prime spots in commercial areas, transit hubs, or residential zones based on your target audience[2].
            </li>
            <li>
              <strong>Size and Format:</strong> Standard billboard sizes in Delhi range from 20x10 feet to 40x20 feet. Digital billboards are available at premium locations[1].
            </li>
            <li>
              <strong>Budget Planning:</strong> Consider factors like location premium, duration, and season. Festival periods typically command higher rates.
            </li>
            <li>
              <strong>Booking Process:</strong> Visit AdBoardBooking.com to:
              <ul className="ml-6 mt-2">
                <li>Browse available locations</li>
                <li>Check real-time pricing</li>
                <li>View location photos and metrics</li>
                <li>Book instantly online</li>
              </ul>
            </li>
            <li>
              <strong>Creative Design:</strong> Ensure your creative follows local advertising guidelines and stands out in Delhi's vibrant landscape.
            </li>
          </ol>

          <h2 className="text-2xl font-semibold mt-8 mb-4 flex items-center gap-2">
            <Clock className="text-blue-600" />
            Timing and Duration
          </h2>

          <p className="mb-6">
            The minimum booking period for billboards in Delhi is typically one month. However, for maximum impact, a campaign duration of 3-6 months is recommended. Consider timing your campaign during key shopping seasons or festivals for enhanced visibility[1].
          </p>

          <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 mb-8">
            <h3 className="text-xl font-semibold mb-4">Why Choose AdBoardBooking.com?</h3>
            <ul className="space-y-3">
              <li>✓ Instant online booking</li>
              <li>✓ Real-time availability check</li>
              <li>✓ Transparent pricing</li>
              <li>✓ Location analytics</li>
              <li>✓ Automated campaign management</li>
            </ul>
          </Card>

          <h2 className="text-2xl font-semibold mt-8 mb-4 flex items-center gap-2">
            <TrendingUp className="text-blue-600" />
            ROI and Performance Metrics
          </h2>

          <p className="mb-6">
            Delhi's outdoor advertising market has shown consistent growth, with a 6% increase last year and expected growth of 15% this year. The total market size stands at ₹3,085 crore, making it one of India's most lucrative advertising markets[1].
          </p>

          <Separator className="my-8" />

          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Start Your Billboard Campaign Today</h3>
            <p className="mb-4">
              Ready to launch your billboard advertising campaign in Delhi? Visit AdBoardBooking.com to explore available locations and book instantly.
            </p>
            <div className="flex gap-4">
              <Link to="/">
                <Button className="bg-blue-600 text-white">
                Explore Locations
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BillboardAdvertisementDelhi;
